import React from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import { Check } from 'lucide-react';

const PricingPage = () => {
  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: '£0',
      originalPrice: '£25',
      period: 'first month free',
      thenPrice: '£25/month',
      description: 'Perfect for small businesses getting started online',
      features: [
        'Managed website hosting',
        'Client portal access',
        'Basic maintenance & updates',
        'Automatic backups',
        '1 content update per month',
        'Email support'
      ],
      cta: 'Start Free Month',
      highlighted: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '£49',
      period: 'per month',
      description: 'For growing businesses that need more',
      features: [
        'Everything in Starter',
        'Up to 3 updates per month',
        'Priority support',
        'Uptime monitoring',
        'Monthly performance reports',
        'Basic SEO optimization',
        'SSL certificate included'
      ],
      cta: 'Go Professional',
      highlighted: true
    },
    {
      id: 'business',
      name: 'Business',
      price: '£89',
      period: 'per month',
      description: 'Enterprise solutions with premium support',
      features: [
        'Everything in Professional',
        'Unlimited reasonable updates',
        'Enhanced security features',
        'Azure/Microsoft 365 integration',
        'Priority feature requests',
        'Dedicated account manager',
        'Advanced analytics',
        'Custom development hours included'
      ],
      cta: 'Contact Sales',
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      <PublicNav />
      
      <section className="pt-32 pb-20 px-6 md:px-12" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold font-['Syne'] text-white mb-6" data-testid="pricing-title">
            Simple, <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 to-purple-400">Transparent</span> Pricing
          </h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto mb-4" data-testid="pricing-description">
            Choose the plan that fits your needs. All plans include access to our exclusive client portal.
          </p>
          <div className="inline-flex items-center space-x-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2">
            <span className="text-sm text-green-300 font-medium">✨ First month free on Starter plan!</span>
          </div>
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12" data-testid="pricing-cards">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`rounded-2xl p-8 transition-all duration-500 ${
                plan.highlighted
                  ? 'bg-gradient-to-b from-indigo-500/20 to-purple-500/20 border-2 border-indigo-500/50 transform scale-105 glow'
                  : 'bg-slate-900/50 border border-white/10 hover:border-indigo-500/30'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.highlighted && (
                <div className="text-center mb-4">
                  <span className="inline-block bg-indigo-500 text-white text-xs font-semibold px-3 py-1 rounded-full" data-testid="popular-badge">
                    MOST POPULAR
                  </span>
                </div>
              )}
              
              <h3 className="text-2xl font-bold font-['Syne'] text-white mb-2" data-testid={`plan-name-${plan.id}`}>{plan.name}</h3>
              <p className="text-slate-400 text-sm mb-6">{plan.description}</p>
              
              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-5xl font-bold text-white" data-testid={`plan-price-${plan.id}`}>{plan.price}</span>
                  <span className="text-slate-400 ml-2">{plan.period}</span>
                </div>
                {plan.thenPrice && (
                  <p className="text-sm text-slate-500 mt-2">then {plan.thenPrice}</p>
                )}
                {plan.originalPrice && plan.id === 'starter' && (
                  <p className="text-sm text-slate-500 mt-1">
                    <span className="line-through">{plan.originalPrice}/month</span>
                  </p>
                )}
              </div>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3" data-testid={`feature-${plan.id}-${idx}`}>
                    <Check className="text-indigo-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-slate-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Link 
                to="/contact" 
                className={`block w-full text-center font-semibold px-6 py-3 rounded-full transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-white text-black hover:bg-slate-200 transform hover:scale-105'
                    : 'bg-transparent border border-white/20 text-white hover:bg-white/10'
                }`}
                data-testid={`cta-${plan.id}`}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-32 px-6 md:px-12" data-testid="pricing-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6" data-testid="faq-1">
              <h3 className="text-lg font-semibold text-white mb-2">How does the first month free work?</h3>
              <p className="text-slate-400">The Starter plan is completely free for your first month. After that, you'll be automatically billed £25/month. You can cancel anytime before the end of your free month with no charges.</p>
            </div>
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6" data-testid="faq-2">
              <h3 className="text-lg font-semibold text-white mb-2">Can I upgrade or downgrade my plan?</h3>
              <p className="text-slate-400">Absolutely! You can upgrade or downgrade anytime through your client portal. Upgrades take effect immediately, while downgrades take effect at your next billing cycle.</p>
            </div>
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6" data-testid="faq-3">
              <h3 className="text-lg font-semibold text-white mb-2">What payment methods do you accept?</h3>
              <p className="text-slate-400">We accept all major credit/debit cards through our secure payment system. You can manage your payment methods in your client portal.</p>
            </div>
            <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6" data-testid="faq-4">
              <h3 className="text-lg font-semibold text-white mb-2">What are "reasonable updates"?</h3>
              <p className="text-slate-400">For Business plan, "reasonable updates" means content changes, bug fixes, and minor feature adjustments. Major redesigns or custom development projects would be quoted separately.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 px-6" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center text-slate-500">
          <p>&copy; 2026 DH HOME NETWORKING AND WEBSITES. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
